import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "../i18n/I18nProvider";
import { GLOBAL_PRESENCE } from "../constants/testIds";
import { Reveal } from "../components/Reveal";

// Approximate normalized lat/long mapped to viewBox (0-1000 x 0-500)
const POINTS = [
  { slug: "brazil", x: 360, y: 330, label: "São Paulo" },
  { slug: "usa", x: 230, y: 200, label: "San Francisco" },
  { slug: "europe", x: 510, y: 165, label: "London" },
  { slug: "middle-east", x: 600, y: 230, label: "Dubai" },
  { slug: "asia", x: 770, y: 240, label: "Singapore" },
];

// A simple stylized dotted world map (dots forming continents silhouette)
// Procedurally generated dot grid masked to a hand-drawn polygon approximation of continents.
function DotMap() {
  const dots = [];
  const step = 11;
  // Rough continental polygons (very approximate, monochrome aesthetic)
  const polys = [
    // North America
    [[140,90],[290,80],[330,170],[310,250],[230,290],[170,260],[120,180]],
    // South America
    [[300,290],[400,290],[420,420],[370,490],[330,470],[300,360]],
    // Europe
    [[470,90],[600,90],[610,170],[560,210],[500,200],[470,160]],
    // Africa
    [[490,210],[620,210],[640,330],[600,430],[540,440],[500,360]],
    // Middle East
    [[610,200],[680,200],[690,260],[640,280]],
    // Asia
    [[630,80],[880,90],[920,200],[860,300],[760,290],[700,260],[660,200],[630,150]],
    // Oceania
    [[820,360],[920,360],[930,420],[850,430]],
  ];

  const pointInPoly = (x, y, poly) => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i][0], yi = poly[i][1];
      const xj = poly[j][0], yj = poly[j][1];
      const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };

  for (let y = 60; y < 460; y += step) {
    for (let x = 60; x < 960; x += step) {
      for (const poly of polys) {
        if (pointInPoly(x, y, poly)) {
          dots.push({ x, y });
          break;
        }
      }
    }
  }

  return (
    <g>
      {dots.map((d, i) => (
        <circle key={i} cx={d.x} cy={d.y} r="1.2" fill="rgba(255,255,255,0.18)" />
      ))}
    </g>
  );
}

export const GlobalPresence = () => {
  const { t } = useI18n();
  const regionMap = Object.fromEntries(t.global.regions.map((r) => [r.slug, r.name]));

  return (
    <section
      id="global"
      data-testid={GLOBAL_PRESENCE.section}
      className="relative bg-black py-32 md:py-44 lg:py-52 px-6 md:px-10 lg:px-14 border-t border-white/5"
    >
      <div className="mx-auto max-w-[1440px]">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 items-end mb-16 md:mb-20">
          <Reveal>
            <div className="flex items-center gap-4 text-[10px] tracking-[0.4em] uppercase font-mono text-white/50">
              <span className="w-8 h-px bg-white/30" />
              {t.global.eyebrow}
            </div>
            <h2 className="mt-8 font-display tracking-display text-white text-[30px] sm:text-[40px] md:text-[52px] lg:text-[60px] leading-[1.05] font-medium max-w-[900px]">
              {t.global.title}
            </h2>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="relative border border-white/10 bg-[#040404] p-4 md:p-8">
            <svg viewBox="0 0 1000 500" className="w-full h-auto" role="img" aria-label="GAELON global presence">
              <DotMap />
              {POINTS.map((p, i) => (
                <g key={p.slug} data-testid={GLOBAL_PRESENCE.region(p.slug)}>
                  <motion.circle
                    cx={p.x}
                    cy={p.y}
                    r="14"
                    fill="rgba(255,255,255,0.06)"
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: [0, 1.6, 1], opacity: [0, 0.6, 0.3] }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.4, delay: i * 0.15, repeat: Infinity, repeatDelay: 1.2 }}
                    style={{ transformOrigin: `${p.x}px ${p.y}px` }}
                  />
                  <motion.circle
                    cx={p.x}
                    cy={p.y}
                    r="3.5"
                    fill="#FFFFFF"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.15 }}
                    style={{ transformOrigin: `${p.x}px ${p.y}px` }}
                  />
                  <text
                    x={p.x + 12}
                    y={p.y + 4}
                    fontSize="11"
                    fill="rgba(255,255,255,0.7)"
                    style={{ fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.12em", textTransform: "uppercase" }}
                  >
                    {regionMap[p.slug] || p.slug}
                  </text>
                </g>
              ))}
            </svg>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4 border-t border-white/8 pt-6">
              {t.global.regions.map((r) => (
                <div key={r.slug} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/65">
                    {r.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default GlobalPresence;
